<template>
  <v-card class="shop-statistic-card">
    <v-card-title class="shop-domain">{{ shopStatistic.domain }}</v-card-title>
    <v-card-text class="card-container">
      <!-- Flache Stat-Zeile im Stil der KPI-Kacheln -->
      <div class="shop-stats-row">
        <div class="shop-stat shop-stat--wide">
          <div class="shop-stat__label">Gewinn</div>
          <div class="shop-stat__value" :class="totalProfit > 0 ? 'shop-stat__value--good' : 'shop-stat__value--bad'">
            {{ totalProfitString }}
          </div>
          <div v-if="comparisonShopStatistic" class="shop-stat__foot">
            <ComparisonBadge
              :current="totalProfit"
              :previous="comparisonProfit"
              unit="€"
              :loading="statisticComparisonLoading"
            />
            <span class="shop-stat__prev">Vgl. {{ comparisonProfitString }}</span>
          </div>
        </div>
        <div class="shop-stat">
          <div class="shop-stat__label">Refunds</div>
          <div class="shop-stat__value">{{ shopStatistic?.refunds?.totalNumber || 0 }}</div>
        </div>
        <div class="shop-stat">
          <div class="shop-stat__label">Refund-Summe</div>
          <div class="shop-stat__value">{{ refundRevenueString }}</div>
        </div>
      </div>
      <v-row>
        <v-col cols="12" md="6">
          <OverviewCard class="mt-3" light title="Details" :switch-data="costsChartData"
          />
        </v-col>
        <v-col cols="12" md="6"
          >
          <v-card light class="ads-insights mt-3 mb-3">
            <v-card-title class="ads-insights__header">
              <span>Werbekosten</span>
              <div class="d-flex align-center">
                <v-btn-toggle v-model="roasSource" mandatory dense>
                  <v-btn x-small value="shop">Shop</v-btn>
                  <v-btn x-small value="platform">Plattform</v-btn>
                </v-btn-toggle>
                <v-btn class="ml-2" x-small outlined color="primary" @click="showAdInsights = true">
                  <v-icon x-small left>mdi-poll</v-icon>
                  Anzeigen
                </v-btn>
              </div>
            </v-card-title>
            <v-card-text>
              <BarChart :aspect-ratio="1.5" :data="adSpendData" />
              <div class="channel-tiles">
                <div v-for="ch in channelInsights" :key="ch.key" class="channel-tile">
                  <div class="channel-tile__header">
                    <span class="channel-tile__dot" :style="{ backgroundColor: ch.color }"></span>
                    <span class="channel-tile__label">{{ ch.label }}</span>
                    <span class="channel-tile__roas" :style="{ backgroundColor: ch.roasColor }">ROAS {{ ch.roasString }}</span>
                  </div>
                  <div class="channel-tile__metric">
                    <span class="channel-tile__metric-label">Spend</span>
                    <span class="channel-tile__metric-value">{{ ch.spendString }}</span>
                  </div>
                  <div class="channel-tile__metric">
                    <span class="channel-tile__metric-label">Conversions</span>
                    <span class="channel-tile__metric-value">{{ ch.conversionsString }}</span>
                  </div>
                  <div class="channel-tile__metric">
                    <span class="channel-tile__metric-label">Cost / Conv.</span>
                    <span class="channel-tile__metric-value">{{ ch.cpaString }}</span>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
          </v-col>
      </v-row>
    </v-card-text>
    <AdInsightsDialog
      v-model="showAdInsights"
      :shop-code="shopStatistic.shopCode"
      :domain="shopStatistic.domain"
      :break-even-roas="breakEvenRoas"
      :shop-revenue="shopStatistic.revenue ? shopStatistic.revenue.total : null"
      :shop-profit="totalProfit"
    />


  </v-card>
</template>

<script>
import { getDecimalNumberString, getRandomColor, getRateColor } from "@/utils";
import OverviewCard from "@/components/Statistics/StatisticOverview/OverviewCard";
import { statisticsColors } from "@/config";
import BarChart from "@/components/BarChart";
import ComparisonBadge from "@/components/Statistics/components/ComparisonBadge";
import AdInsightsDialog from "@/components/Statistics/StatisticByShops/AdInsightsDialog";
import { mapGetters } from "vuex";

const _ = require("lodash");

export default {
  name: "ShopStatisticCard",
  components: {AdInsightsDialog, ComparisonBadge, BarChart, OverviewCard },
  props: {
    shopStatistic: Object,
    comparisonShopStatistic: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      roasSource: "shop",
      showAdInsights: false,
    };
  },
  computed: {
    ...mapGetters(["statisticComparisonLoading"]),
    totalProfit() {
      return this.computeProfit(this.shopStatistic);
    },
    comparisonProfit() {
      return this.comparisonShopStatistic
        ? this.computeProfit(this.comparisonShopStatistic)
        : null;
    },
    // ROAS below which ad spend loses money after all non-ad costs:
    // revenue / (revenue - non-ad costs). Null when the margin is non-positive.
    breakEvenRoas() {
      const revenue = this.shopStatistic.revenue?.total || 0;
      const nonAdCosts = Object.keys(this.shopStatistic.costs || {})
        .filter((key) => key !== "advertisement")
        .reduce((sum, key) => sum + (this.shopStatistic.costs[key] || 0), 0);
      const margin = revenue - nonAdCosts;
      return revenue > 0 && margin > 0 ? revenue / margin : null;
    },
    channelInsights() {
      const byChannel = this.shopStatistic?.advertisement?.byChannel || {};
      const labelMap = {
        facebook: "Facebook",
        instagram: "Instagram",
        google: "Google",
        tiktok: "TikTok",
      };

      return Object.keys(byChannel)
        .filter((key) => key !== "refunds")
        .filter((key) => (byChannel[key]?.spend || 0) > 0)
        .map((key) => {
          const channel = byChannel[key] || {};
          const spend = channel.spend || 0;
          const conversions = channel.conversions || 0;
          const conversionValue = channel.conversionValue;

          let revenue = this.shopStatistic.revenue?.bySource?.[key] || 0;
          if (key === "facebook") {
            revenue += this.shopStatistic.revenue?.bySource?.instagram || 0;
          }

          // shop = our order tracking, platform = value reported by the ad platform
          const roas = this.roasSource === "platform"
            ? (conversionValue === undefined || conversionValue === null ? null : conversionValue / spend)
            : revenue / spend;
          const cpa = conversions ? spend / conversions : 0;

          return {
            key,
            label: labelMap[key] || key.charAt(0).toUpperCase() + key.slice(1),
            color: statisticsColors[key] || "#9e9e9e",
            roasColor: roas === null ? "#e0e0e0" : this.getRoasColor(roas),
            spendString: getDecimalNumberString(spend, 2) + " €",
            conversionsString: this.formatCount(conversions),
            roasString: roas === null ? "–" : getDecimalNumberString(roas, 2),
            cpaString: conversions ? getDecimalNumberString(cpa, 2) + " €" : "–",
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    costsChartData() {
      const costs = Object.keys(this.shopStatistic.costs).map((costId) => {
        return {
          label: costId,
          count: this.shopStatistic.costs[costId],
          color: statisticsColors[costId],
        };
      });

      const allCoupons = []
      const afterPrev = {
        label: "After Preview",
        count: 0,
        amount: 0,
        color: getRandomColor('AfterPreview')
      }
      const freePrev = {
        label: "Free Preview",
        count: 0,
        amount: 0,
        color: getRandomColor('FreePreview')
      }

      const loox = {
        label: "Loox",
        count: 0,
        amount: 0,
        color: getRandomColor('Loox')
      }

      const coupons = Object.keys(this.shopStatistic.details.coupons).map(
        (key) => {
          return {
            label: `${this.shopStatistic.details.coupons[key].count} x ${key}`,
            count: -this.shopStatistic.details.coupons[key].amount,
            color: getRandomColor(key),
          };
        }
      );

      coupons.forEach(coupon => {
        if (coupon.label.split(" ")[0] === "1") {
          if (coupon.label.split(" ")[2].slice(0, 4) === "PREV") {
            afterPrev.amount += coupon.count
            afterPrev.count++
          } else if(coupon.count === -6.99) {
            freePrev.amount += coupon.count
            freePrev.count++
          } else if(coupon.label.split(" ")[2].slice(0,3) === "LX-") {
            loox.amount += coupon.count
            loox.count++
          } else {
            if(coupon.label.includes('FULL100') || coupon.label.includes('FULL100')) {
              allCoupons.push(coupon)
            }
          }
        } else {
          if(!coupon.label.includes('FULL100') && !coupon.label.includes('FULL100')) {
            allCoupons.push(coupon)
          }
        }
      })

      if (freePrev.count) {
        allCoupons.push({
          ...freePrev,
          label: `${freePrev.count} x ${freePrev.label}`,
          count: freePrev.amount
        })
      }

      if (loox.count) {
        allCoupons.push({
          ...loox,
          label: `${loox.count} x ${loox.label}`,
          count: loox.amount
        })
      }

      if (afterPrev.count) {
        allCoupons.push({
          ...afterPrev,
          label: `${afterPrev.count} x ${afterPrev.label}`,
          count: afterPrev.amount
        })
      }

      // Varianten: nur das Produkt, absteigend sortiert, Top 8 + "Weitere" —
      // ruhige Ein-Hue-Abstufung statt Zufallsfarben.
      // UI-FALLBACK: Alt-Buckets (vor dem Backend-Fix synchronisiert) tragen
      // die Roh-SKU als Key — die wird hier clientseitig in Produkt/Stil/Theme
      // zerlegt und mit den sauberen Buckets gemergt. Stirbt aus, sobald
      // alte Tage re-synct oder aus den Zeiträumen gerutscht sind.
      const variantBuckets = {};
      const styleBuckets = { ...(this.shopStatistic.details.styles || {}) };
      const themeBuckets = { ...(this.shopStatistic.details.themes || {}) };
      const normalize = (label) => String(label || "").toLowerCase().replace(/\s|cm/g, "");

      for (const [key, entry] of Object.entries(this.shopStatistic.details.printVariants || {})) {
        // Extras sind Zubehör, keine Produkte
        if (String(key).includes("EXT_") || String(entry.label || "").includes("EXT_")) continue;

        const parsed = this.parseLiveSku(key);
        let label = entry.label;
        if (parsed) {
          label = parsed.product;
          styleBuckets[parsed.style] = (styleBuckets[parsed.style] || 0) + entry.count;
          themeBuckets[parsed.theme] = (themeBuckets[parsed.theme] || 0) + entry.count;
        }

        const norm = normalize(label);
        if (!variantBuckets[norm]) variantBuckets[norm] = { label, count: 0 };
        variantBuckets[norm].count += entry.count;
      }
      const printVariants = this.topNBuckets(Object.values(variantBuckets));

      const tabs = [
        {
          label: "Kosten",
          data: costs,
          unit: "€",
        },
        {
          label: "Coupons",
          data: allCoupons,
          unit: "€",
        },
        {
          label: "Varianten",
          data: printVariants,
          unit: "",
        },
      ];

      // Stil/Theme aus den Live-SKU-Codes (live_canvas_45x30_rustic_car) —
      // Backend-Buckets + UI-Fallback aus Alt-Keys, oben bereits gemergt
      const styles = this.topNBuckets(this.bucketEntries(styleBuckets));
      if (styles.length) tabs.push({ label: "Stile", data: styles, unit: "" });
      const themes = this.topNBuckets(this.bucketEntries(themeBuckets));
      if (themes.length) tabs.push({ label: "Themes", data: themes, unit: "" });

      return tabs;
    },
    totalProfitString() {
      return getDecimalNumberString(this.totalProfit, 2) + "€";
    },
    comparisonProfitString() {
      return this.comparisonProfit === null
        ? null
        : getDecimalNumberString(this.comparisonProfit, 2) + " €";
    },
    refundRevenueString() {
      return getDecimalNumberString(-(this.shopStatistic?.refunds?.revenue || 0), 2) + " €";
    },
    adSpendData() {
      const advertisementKeys = Object.keys(
        this.shopStatistic.advertisement.byChannel
      ).filter(key => key !== 'refunds');
      const revenueKeys = Object.keys(this.shopStatistic.revenue.bySource);

      const channels = _.union(advertisementKeys, revenueKeys);

      const labels = channels.map((label) => {
        if (label === "facebook") {
          return "FB & IG";
        } else {
          return label;
        }
      });
      const chartData = {
        labels: labels,
        datasets: [
          {
            label: "Ad Spend",
            backgroundColor: "#d70000",
            data: channels.map(
              (key) =>
                this.shopStatistic.advertisement.byChannel[key]?.spend || 0
            ), // Ad spend for each channel
          },
          {
            label: "Generated Revenue",
            backgroundColor: "#43a653",
            data: channels.map((key) => {
              if (key === "facebook") {
                return (
                  (this.shopStatistic.revenue.bySource?.facebook || 0) +
                  (this.shopStatistic.revenue.bySource?.instagram || 0)
                );
              } else {
                return this.shopStatistic.revenue.bySource?.[key] || 0;
              }
            }), // Ad revenue for each channel
          },
        ],
      };

      return chartData;
    },
  },
  methods: {
    bucketEntries(bucket) {
      return Object.entries(bucket || {}).map(([label, count]) => ({ label, count }));
    },
    /** Roh-Live-SKU zerlegen: live_canvas_45x30_original_people →
     *  { product: "Leinwand 45x30cm", style: "original", theme: "people" }.
     *  Kennt bewusst auch die historischen Tippfehler-SKUs. */
    parseLiveSku(key) {
      if (!/^live_/.test(String(key))) return null;
      const parts = String(key).split("_");
      if (parts.length < 5) return null;
      const productNames = { canvas: "Leinwand", cancvs: "Leinwand", cancas: "Leinwand", poster: "Poster" };
      const type = productNames[parts[1]] || parts[1];
      return {
        product: `${type} ${parts[2]}cm`,
        style: parts[3],
        theme: parts.slice(4).join("_"),
      };
    },
    /** Absteigend sortieren, Top 8 behalten, Rest als "Weitere" bündeln;
     *  Farbe deterministisch aus dem Label (getRandomColor) — dieselbe
     *  Variante/Stil/Theme hat damit überall und dauerhaft dieselbe Farbe */
    topNBuckets(entries, limit = 8) {
      const sorted = [...entries].sort((a, b) => b.count - a.count);
      const top = sorted.slice(0, limit).map((entry) => ({
        ...entry,
        color: getRandomColor(entry.label),
      }));
      const rest = sorted.slice(limit);
      if (rest.length) {
        top.push({
          label: `Weitere (${rest.length})`,
          count: rest.reduce((sum, entry) => sum + entry.count, 0),
          color: "#d3d7dd",
        });
      }
      return top;
    },
    computeProfit(shop) {
      const totalCosts = Object.keys(shop.costs || {}).reduce(
        (total, component) => total + (shop.costs[component] || 0),
        0
      );
      return Number(((shop.revenue?.total || 0) - totalCosts).toFixed(2));
    },
    getRoasColor(roas) {
      return getRateColor(3.5, 0, roas);
    },
    formatCount(value) {
      return Number(value).toLocaleString("de-DE", { maximumFractionDigits: 1 });
    },
  },
};
</script>

<style lang="scss" scoped>
.shop-stats-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 10px;
  margin-bottom: 4px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr 1fr;

    .shop-stat--wide {
      grid-column: 1 / -1;
    }
  }
}

.shop-stat {
  background: #f6f8fa;
  border-radius: 12px;
  padding: 10px 14px;

  &__label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: rgba(0, 0, 0, 0.45);
  }

  &__value {
    font-size: 20px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;

    &--good {
      color: #1b7f4d;
    }

    &--bad {
      color: #c23434;
    }
  }

  &__foot {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 2px;
  }

  &__prev {
    font-size: 11.5px;
    color: rgba(0, 0, 0, 0.45);
  }
}

.shop-statistic-card {
  box-shadow: rgba(0, 0, 0, 0.07) 0 1px 2px, rgba(0, 0, 0, 0.07) 0 2px 4px, rgba(0, 0, 0, 0.07) 0 4px 8px, rgba(0, 0, 0, 0.07) 0 8px 16px, rgba(0, 0, 0, 0.07) 0 16px 32px, rgba(0, 0, 0, 0.07) 0 32px 64px !important;

  @include md() {
    box-shadow: rgba(14, 63, 126, 0.04) 0 0 0 1px, rgba(42, 51, 69, 0.04) 0 1px 1px -0.5px, rgba(42, 51, 70, 0.04) 0 3px 3px -1.5px, rgba(42, 51, 70, 0.04) 0 6px 6px -3px, rgba(14, 63, 126, 0.04) 0 12px 12px -6px, rgba(14, 63, 126, 0.04) 0 24px 24px -12px !important;
  }

  .profit-comparison {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;

    &__prev {
      font-size: 12px;
      font-weight: lighter;
      color: #757575;
      white-space: nowrap;
    }
  }

  .refund-container {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin: 12px 0;
  }

  .shop-domain {
    display: flex;
    justify-content: center;
    font-weight: bolder;
    font-size: 24px;
    width: 100%;
  }

  .card-container {
    display: flex;
    justify-content: center;
    align-items: stretch;
    flex-direction: column;
  }

  .ads-insights {
    width: 100%;

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 6px;
    }
  }

  .channel-tiles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 10px;
    margin-top: 16px;
  }

  .channel-tile {
    border: 1px solid #ececec;
    border-radius: 10px;
    padding: 12px;
    background-color: #fafafa;

    &__header {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 10px;
    }

    &__dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    &__label {
      font-weight: 700;
      font-size: 14px;
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__roas {
      color: #2b2b2b;
      font-weight: 700;
      font-size: 11px;
      padding: 2px 7px;
      border-radius: 10px;
      white-space: nowrap;
    }

    &__metric {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      font-size: 12px;

      & + & {
        margin-top: 5px;
      }
    }

    &__metric-label {
      color: #757575;
    }

    &__metric-value {
      font-weight: 600;
      color: #2b2b2b;
    }
  }

  .profit-counter {
    font-size: 28px;
    text-align: center;
    color: #43a653;
    font-weight: bolder;
    padding: 16px 16px;
    width: 100%;

    &--negative {
      color: #d70000;
    }
  }
}
</style>
